import React, { createContext, useState, useContext, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { api } from '../api';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });


  const theme = React.useMemo(() => {
    return createTheme({
      typography: {
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif",
      },
      palette: {
        mode: darkMode ? 'dark' : 'light',
        primary: { main: darkMode ? '#0a84ff' : '#0071e3' },
        success: { main: darkMode ? '#30d158' : '#34c759' },
        error: { main: darkMode ? '#ff453a' : '#ff3b30' },
        background: {
          default: darkMode ? '#3a3a3c' : '#e8e8ed',
          paper: darkMode ? '#4a4a4c' : '#f9f9fb',
        },
        text: {
          primary: darkMode ? '#f0f0f0' : '#1d1d1f',
          secondary: darkMode ? '#a0a0a5' : '#6e6e73',
        },
        divider: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
        action: {
          hover: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
          selected: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        },
      },
      shape: { borderRadius: 10 },
      components: {
        MuiButton: {
          styleOverrides: {
            root: { boxShadow: 'none', textTransform: 'none', fontWeight: 500, '&:hover': { boxShadow: 'none' } },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: { borderRadius: 10, backgroundColor: darkMode ? '#2c2c2e' : '#ffffff' },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 16,
              backgroundImage: 'none',
              backgroundColor: darkMode ? '#4a4a4c' : '#f9f9fb',
              boxShadow: darkMode
                ? '0 1px 0 rgba(255,255,255,0.04), 0 2px 8px rgba(0,0,0,0.15)'
                : '0 1px 0 rgba(0,0,0,0.04), 0 2px 8px rgba(0,0,0,0.06)',
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: { backgroundImage: 'none', backgroundColor: darkMode ? '#4a4a4c' : '#f9f9fb' },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: { borderRadius: 6, fontSize: '0.78rem', fontWeight: 500, transition: 'none', backgroundColor: darkMode ? '#48484a' : '#e4e4e9', color: darkMode ? '#e0e0e0' : '#1d1d1f' },
            colorSuccess: { transition: 'none', backgroundColor: darkMode ? '#1a3a23' : '#e8f8ed', color: darkMode ? '#30d158' : '#1a7f37' },
          },
        },
        MuiAppBar: { styleOverrides: { root: { boxShadow: 'none', backgroundImage: 'none' } } },
        MuiDialog: {
          styleOverrides: {
            paper: { backgroundImage: 'none', backgroundColor: darkMode ? '#4a4a4c' : '#ffffff' },
          },
        },
        MuiMenu: {
          styleOverrides: {
            paper: {
              backgroundColor: darkMode ? '#4a4a4c' : '#ffffff',
              backgroundImage: 'none',
              borderRadius: 12,
              boxShadow: darkMode ? '0 4px 24px rgba(0,0,0,0.4)' : '0 4px 24px rgba(0,0,0,0.12)',
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              fontSize: '0.9rem',
              '&:hover': { backgroundColor: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
            },
          },
        },
        MuiSlider: { styleOverrides: { root: { color: darkMode ? '#0a84ff' : '#0071e3' } } },
        MuiCheckbox: {
          styleOverrides: {
            root: {
              color: darkMode ? '#48484a' : '#c7c7cc',
              '&.Mui-checked': { color: darkMode ? '#0a84ff' : '#0071e3' },
            },
          },
        },
        MuiLinearProgress: {
          styleOverrides: {
            root: { backgroundColor: darkMode ? '#2c2c2e' : '#e5e5ea', borderRadius: 2, height: 4 },
          },
        },
      },
    });
  }, [darkMode]);

  const toggleDarkMode = () => {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem('darkMode', String(next));
    const token = localStorage.getItem('token');
    if (token) {
      api.updateUser({ dark_mode: next }).catch(() => {});
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      document.body.classList.toggle('dark-mode', darkMode);
      document.body.classList.toggle('light-mode', !darkMode);
    } else {
      document.body.classList.remove('dark-mode');
      document.body.classList.add('light-mode');
    }
  }, [darkMode]);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};